import { Accordion, Typography } from '@sravni/react-design-system';
import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from 'shared/lib';
import { AdaptiveCard } from 'shared/ui/AdaptiveCard';

import { checkIsFormFullFilledSelector } from 'entities/creditForm';
import { wlBlockFFSelector } from 'entities/whiteLabels';

import type { TStepsValidateErrors } from 'widgets/AnketaWidget/lib';
import { useStepper, useSummarySendContact, validateErrorHandlerThunk } from 'widgets/AnketaWidget/lib';
import { useValidateData } from 'widgets/AnketaWidget/lib/useValidateData';
import { validateSuccessHandlerThunk } from 'widgets/AnketaWidget/lib/validateSuccessHandlerThunk';

import { CommonSummary, type ICommonSummaryProps } from '../CommonSummary/CommonSummary';

import styles from './UserDataSummary.module.scss';
import { UserDataSummaryTexts } from './UserDataSummary.texts';
import { UserDataSummarySkeleton } from './UserDataSummarySkeleton';

interface IUserDataSummary extends ICommonSummaryProps {
  isLoading?: boolean;
  toggleIsOpened?: (val: boolean) => void;
  customTitle?: string;
  customSubtitle?: string;
  isHideAccordionData?: boolean;
}

export const UserDataSummaryAccordion: FC<IUserDataSummary> = ({
  isLoading = false,
  toggleIsOpened,
  customTitle,
  customSubtitle,
  onFormSubmit,
  isHideAccordionData,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const { stepsWithMultiStepsOrder } = useStepper(props.steps);
  const { isHideSummary } = useAppSelector(wlBlockFFSelector) || {};

  const onError = useCallback(
    (errors: TStepsValidateErrors) =>
      dispatch(
        validateErrorHandlerThunk(
          errors,
          stepsWithMultiStepsOrder.map(({ formId }) => formId),
        ),
      ),
    [dispatch, stepsWithMultiStepsOrder],
  );

  const onSuccess = useCallback(() => {
    dispatch(validateSuccessHandlerThunk());
    onFormSubmit?.();
  }, [dispatch, onFormSubmit]);

  const isValidating = useValidateData({
    onSuccess,
    onError,
  });
  const isFormReady = useAppSelector(checkIsFormFullFilledSelector);

  useSummarySendContact();

  if (isLoading || isValidating) {
    return <UserDataSummarySkeleton />;
  }

  return (
    <AdaptiveCard
      className={styles.accordion}
      color="light"
    >
      <Accordion
        defaultIndex={isHideSummary ? undefined : [0]}
        padding={false}
        onChange={(openAccordionItems: number[]) => {
          toggleIsOpened?.(openAccordionItems.length > 0);
        }}
      >
        <Accordion.Item
          title={
            <Typography.Text
              strong
              size={16}
            >
              {customTitle || UserDataSummaryTexts.caption}
            </Typography.Text>
          }
          subtitle={!isFormReady ? undefined : customSubtitle}
          unmountOnExit={false}
        >
          <CommonSummary
            onFormSubmit={onFormSubmit}
            isHideAccordionData={isHideAccordionData}
            {...props}
          />
        </Accordion.Item>
      </Accordion>
    </AdaptiveCard>
  );
};
